import { Controller } from "@hotwired/stimulus";

// Resolves the availability state of every booking position in one request.
//
// Deciding whether a position is still bookable, or its availability blockable,
// means simulating a carve server-side — seconds of work per position. Doing that
// while rendering the booking page is what made it slow, so the page renders the
// block buttons disabled and the "not available" notices hidden, and this
// controller fills both in afterwards.
//
// Deliberately one request for the whole booking rather than one per position:
// each check is expensive, and a fetch per position would occupy a Puma thread
// for each one on every page view.
export default class extends Controller {
  static targets = ["blockButton", "unavailableNotice"];

  static values = { url: String };

  connect() {
    this.load();
  }

  disconnect() {
    this.abortController?.abort();
    this.abortController = null;
  }

  async load() {
    if (!this.hasUrlValue) return;

    this.abortController = new AbortController();

    try {
      const response = await fetch(this.urlValue, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const { states } = await response.json();
      this.apply(states || {});
    } catch (error) {
      if (error.name === "AbortError") return;

      // Leave the buttons disabled and the notices hidden. Both are the
      // conservative reading, and #block re-checks server-side regardless.
      console.error(
        "[BookingResourceSkuAvailability] Could not load availability states:",
        error
      );
    }
  }

  apply(states) {
    this.blockButtonTargets.forEach((button) => {
      const state = states[button.dataset.bookingResourceSkuId];
      if (state?.blockable) button.removeAttribute("disabled");
    });

    this.unavailableNoticeTargets.forEach((notice) => {
      const state = states[notice.dataset.bookingResourceSkuId];
      if (state && !state.bookable) notice.classList.remove("hidden");
    });
  }
}
